import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Building2, Lock, ShieldCheck, ShieldAlert, CheckCircle2,
  AlertTriangle, Upload, Plus, Trash2, Phone, Mail, MapPin,
  Loader2, Check, Download, FileText, ArrowRight, RefreshCw, Send
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const REQUIRED_KYC_DOCS = [
  // 1. Statutory & Tax Registrations
  {
    key: 'IEC',
    label: 'IEC',
    title: 'IEC (Import Export Code)',
    category: 'Statutory & Tax Registrations',
    desc: 'Mandatory 10-digit code issued by DGFT for cross-border logistics',
    patterns: ['iec', 'import export code', 'import_export_code', 'ieccopy', 'iec copy'],
    isAgentOnly: false,
  },
  {
    key: 'PAN CARD',
    label: 'PAN CARD',
    title: 'Company PAN Card',
    category: 'Statutory & Tax Registrations',
    desc: 'Permanent Account Number copy of entity',
    patterns: ['pan card', 'pancard', 'pan', 'pan copy'],
    isAgentOnly: false,
  },
  {
    key: 'GST COPY',
    label: 'GST COPY',
    title: 'GST Registration Certificate',
    category: 'Statutory & Tax Registrations',
    desc: 'Form GST REG-06 or official GST registration certificate',
    patterns: ['gst copy', 'gstcopy', 'gst certificate', 'gst', 'gstin'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of PAN Allotment',
    label: 'Copy of PAN Allotment',
    title: 'Copy of PAN Allotment',
    category: 'Statutory & Tax Registrations',
    desc: 'PAN Allotment Letter / NSDL / UTI allotment copy',
    patterns: ['copy of pan allotment', 'pan allotment', 'pan allotment letter', 'pan_allotment', 'pan allotment copy'],
    isAgentOnly: false,
  },

  // 2. Constitutional & Legal Documents
  {
    key: 'Certificate of Incorporation',
    label: 'Certificate of Incorporation',
    title: 'Certificate of Incorporation (COI)',
    category: 'Constitutional & Legal Documents',
    desc: 'MCA Certificate of Incorporation / Partnership Deed / Registration',
    patterns: ['certificate of incorporation', 'coi', 'incorporation certificate', 'registration certificate', 'partnership deed'],
    isAgentOnly: false,
  },
  {
    key: 'Memorandum of Association',
    label: 'Memorandum of Association',
    title: 'Memorandum of Association (MOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Memorandum of Association',
    patterns: ['memorandum of association', 'moa'],
    isAgentOnly: false,
  },
  {
    key: 'Articles of Association',
    label: 'Articles of Association',
    title: 'Articles of Association (AOA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Certified copy of company Articles of Association',
    patterns: ['articles of association', 'aoa'],
    isAgentOnly: false,
  },
  {
    key: 'Power of Attorney',
    label: 'Power of Attorney',
    title: 'Power of Attorney (POA)',
    category: 'Constitutional & Legal Documents',
    desc: 'Power of Attorney or Board Resolution in favor of authorized signatory',
    patterns: ['power of attorney', 'poa', 'board resolution', 'authority letter'],
    isAgentOnly: false,
  },

  // 3. Authorizations, Identification & Address Proof
  {
    key: 'CHA Authority Letter',
    label: 'CHA Authority Letter',
    title: 'CHA Authority Letter',
    category: 'Authorizations & Address Proof',
    desc: 'Customs Broker authorization issued on company letterhead',
    patterns: ['cha authority letter', 'cha authority', 'cha letter', 'cha_authority_letter'],
    isAgentOnly: false,
  },
  {
    key: 'KYC Form',
    label: 'KYC Form',
    title: 'KYC Form',
    category: 'Authorizations & Address Proof',
    desc: 'Duly filled, signed & stamped customer onboarding KYC format',
    patterns: ['kyc form', 'kycform', 'kyc'],
    isAgentOnly: false,
  },
  {
    key: 'AADHAR CARD',
    label: 'AADHAR CARD',
    title: 'Aadhar Card',
    category: 'Authorizations & Address Proof',
    desc: 'Identification proof of authorized signatory / director',
    patterns: ['aadhar card', 'aadhaar card', 'aadhar', 'aadhaar', 'adhar card', 'adhar'],
    isAgentOnly: false,
  },
  {
    key: 'Copy of Telephone Bill',
    label: 'Copy of Telephone Bill',
    title: 'Copy of Telephone Bill',
    category: 'Authorizations & Address Proof',
    desc: 'Telephone / Electricity / Utility bill as registered office address proof',
    patterns: ['copy of telephone bill', 'telephone bill', 'phone bill', 'utility bill', 'electricity bill', 'telephone_bill'],
    isAgentOnly: false,
  },
  {
    key: 'IF AGENT AGREEMENT COPY',
    label: 'IF AGENT AGREEMENT COPY',
    title: 'Agent Agreement Copy',
    category: 'Agency Representation',
    desc: 'Executed agency or representation contract agreement',
    patterns: ['agent agreement', 'agent agreement copy', 'if agent agreement copy', 'agreement copy', 'agent_agreement_copy'],
    isAgentOnly: true,
  },
];

export default function PublicKycOnboarding() {
  const { token } = useParams();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [panNumber, setPanNumber] = useState('');
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [savingBranches, setSavingBranches] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('kyc'); // 'kyc' | 'branches'

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const fetchOnboardingData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${backendUrl}/api/public/kyc-onboarding/${token}`);
      if (!res.ok) {
        throw new Error('Invalid or expired KYC link. Please contact support.');
      }
      const json = await res.json();
      setData(json);
      setPanNumber(json.pan_number || '');
      setBranches(json.branches && json.branches.length > 0 ? json.branches : [
        {
          id: `new-${Date.now()}`,
          is_head_office: true,
          branch_nick_name: 'Head Office',
          address_line_1: '',
          address_line_2: '',
          city: '',
          state: '',
          country: 'India',
          pincode: '',
          gst_number: '',
          contact_person: '',
          designation: '',
          contact_numbers: [''],
          emails: ['']
        }
      ]);
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [backendUrl, token, toast]);

  useEffect(() => {
    fetchOnboardingData();
  }, [fetchOnboardingData]);

  const isLocked = data?.kyc_locked && !data?.rekyc_requested;
  const isAgent = (data?.business_roles || []).some(r => String(r).toUpperCase() === 'AGENT');
  const activeRequiredDocs = REQUIRED_KYC_DOCS.filter(d => !d.isAgentOnly || isAgent);

  const getUploadedDoc = (docConfig) => {
    return (data?.kyc_documents || []).find(d => {
      const name = (d.document_name || '').trim().toLowerCase();
      return docConfig.patterns.some(p => name === p || name.includes(p));
    });
  };

  const completedCount = activeRequiredDocs.filter(d => !!getUploadedDoc(d)).length;
  const totalRequired = activeRequiredDocs.length;
  const progressPercent = totalRequired > 0 ? Math.round((completedCount / totalRequired) * 100) : 0;
  const isFullyVerified = totalRequired > 0 && completedCount === totalRequired;

  // Branch Handlers
  const handleBranchChange = (index, field, value) => {
    setBranches(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleContactNumberChange = (bIndex, cIndex, val) => {
    setBranches(prev => {
      const updated = [...prev];
      const numbers = [...(updated[bIndex].contact_numbers || [''])];
      numbers[cIndex] = val;
      updated[bIndex].contact_numbers = numbers;
      return updated;
    });
  };

  const handleEmailChange = (bIndex, eIndex, val) => {
    setBranches(prev => {
      const updated = [...prev];
      const emails = [...(updated[bIndex].emails || [''])];
      emails[eIndex] = val;
      updated[bIndex].emails = emails;
      return updated;
    });
  };

  const addBranch = () => {
    setBranches(prev => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        is_head_office: false,
        branch_nick_name: `Branch ${prev.length + 1}`,
        address_line_1: '',
        address_line_2: '',
        city: '',
        state: '',
        country: 'India',
        pincode: '',
        gst_number: '',
        contact_person: '',
        designation: '',
        contact_numbers: [''],
        emails: ['']
      }
    ]);
  };

  const removeBranch = (index) => {
    if (branches.length <= 1) {
      toast({ title: 'Notice', description: 'At least one branch (Head Office) is required.' });
      return;
    }
    setBranches(prev => prev.filter((_, i) => i !== index));
  };

  const saveBranches = async () => {
    if (isLocked) {
      toast({ title: 'Locked', description: 'Branch details are submitted and locked.', variant: 'destructive' });
      return;
    }

    try {
      setSavingBranches(true);
      const res = await fetch(`${backendUrl}/api/public/kyc-onboarding/${token}/branches`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pan_number: panNumber,
          branches: branches.map(b => ({
            ...b,
            id: String(b.id).startsWith('new-') ? null : b.id
          }))
        })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to save branch details');
      }

      toast({ title: 'Success', description: 'Branch and contact details saved successfully.' });
      fetchOnboardingData();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setSavingBranches(false);
    }
  };

  // Document Upload Handler
  const handleUploadDoc = async (docName, file) => {
    if (!file) return;
    if (isLocked) {
      toast({ title: 'Locked', description: 'Documents are locked from further edits.', variant: 'destructive' });
      return;
    }

    try {
      setUploadingDoc(docName);
      const fd = new FormData();
      fd.append('file', file);

      const res = await fetch(`${backendUrl}/api/public/kyc-onboarding/${token}/documents?document_name=${encodeURIComponent(docName)}`, {
        method: 'POST',
        body: fd
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to upload document');
      }

      toast({ title: 'Uploaded', description: `${docName} uploaded and verified successfully.` });
      fetchOnboardingData();
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploadingDoc(null);
    }
  };

  // Submit and Lock
  const handleSubmitKyc = async () => {
    if (completedCount === 0) {
      toast({
        title: 'No Documents Uploaded',
        description: 'Please upload the applicable KYC documents for your entity before submitting.',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      const res = await fetch(`${backendUrl}/api/public/kyc-onboarding/${token}/submit`, {
        method: 'POST'
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to submit KYC form');
      }

      toast({
        title: 'KYC Submitted Successfully',
        description: 'Your KYC onboarding is submitted. All documents are now securely locked for verification.'
      });
      fetchOnboardingData();
    } catch (err) {
      toast({ title: 'Submission Error', description: err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-ibm">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-accent-blue animate-spin mx-auto mb-3" />
          <p className="text-slate-600 font-medium">Loading secure KYC onboarding portal...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-ibm">
        <Card className="max-w-md w-full p-8 text-center bg-white shadow-lg rounded-2xl border-0">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 text-red-600">
            <AlertTriangle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-manrope">Invalid or Expired Link</h2>
          <p className="text-slate-500 text-sm mt-2">
            This KYC onboarding link is invalid or has expired. Please contact Stavan Logistics or request a fresh link.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/70 font-ibm text-slate-800 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Top Header Card */}
        <Card className="bg-slate-900 text-white rounded-2xl shadow-xl overflow-hidden border-0">
          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                  <Building2 className="w-6 h-6" />
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-blue-400 font-manrope">
                    Stavan Logistics & Freight Forwarding
                  </div>
                  <h1 className="text-2xl font-bold font-manrope text-white">
                    Company KYC Compliance Portal
                  </h1>
                </div>
              </div>

              {/* Status Badge */}
              <div>
                {data.rekyc_requested ? (
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    <RefreshCw className="w-4 h-4 animate-spin text-amber-300" />
                    Re-KYC Requested by Admin
                  </span>
                ) : isLocked ? (
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                    <Lock className="w-4 h-4 text-emerald-400" />
                    Submitted & Locked for Verification
                  </span>
                ) : isFullyVerified ? (
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/40">
                    <CheckCircle2 className="w-4 h-4 text-blue-400" />
                    Ready for Final Submission
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-amber-500/20 text-amber-300 border border-amber-500/40">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                    KYC Pending ({completedCount}/{totalRequired})
                  </span>
                )}
              </div>
            </div>

            {/* FIXED & LOCKED Customer Name Notice */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 bg-slate-800/80 border border-slate-700/80 rounded-xl p-4 flex items-start gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400 shrink-0 mt-0.5">
                  <Lock className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    Customer / Legal Entity Name (Fixed by Compliance)
                  </div>
                  <div className="text-lg font-bold text-white font-manrope mt-0.5">
                    {data.business_name}
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    This entity name is pre-registered and cannot be edited. Please submit branch details and mandatory KYC documents below.
                  </div>
                </div>
              </div>

              <div className="bg-slate-800/80 border border-slate-700/80 rounded-xl p-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  Business Roles
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(data.business_roles || ['SHIPPER']).map((r, i) => (
                    <span
                      key={i}
                      className="px-2 py-0.5 rounded text-xs font-semibold bg-blue-500/20 text-blue-300 border border-blue-500/30 uppercase tracking-wider"
                    >
                      {r}
                    </span>
                  ))}
                </div>
                <div className="text-[11px] text-slate-400 mt-2 font-mono">
                  {data.pan_number ? `PAN: ${data.pan_number}` : 'PAN: Not Registered'}
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Re-KYC Alert Banner if requested */}
        {data.rekyc_requested && (
          <div className="p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-3 shadow-sm">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-amber-900 text-sm font-manrope">
                Compliance Officer Requested Re-KYC
              </h4>
              <p className="text-amber-800 text-xs mt-0.5">
                Remarks: <span className="font-semibold">{data.rekyc_remarks || 'Please upload updated KYC documents.'}</span>
              </p>
              <p className="text-amber-700 text-[11px] mt-1">
                You can now re-upload the requested documents and click "Submit KYC for Verification" once complete.
              </p>
            </div>
          </div>
        )}

        {/* Locked Banner */}
        {isLocked && (
          <div className="p-4 bg-emerald-50 border border-emerald-300 rounded-xl flex items-start gap-3 shadow-sm">
            <Lock className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-emerald-900 text-sm font-manrope">
                KYC Documents Locked for Compliance Review
              </h4>
              <p className="text-emerald-800 text-xs mt-0.5">
                Your documents have been submitted on {data.kyc_submitted_at ? new Date(data.kyc_submitted_at).toLocaleDateString() : 'recently'} and are under verification.
                Editing is locked to preserve legal compliance.
              </p>
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="flex border-b border-slate-200 gap-4">
          <button
            type="button"
            onClick={() => setActiveTab('kyc')}
            className={`pb-3 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'kyc'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <ShieldCheck className="w-4 h-4" />
            KYC Documents Checklist ({completedCount}/{totalRequired})
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('branches')}
            className={`pb-3 text-sm font-semibold transition-all border-b-2 flex items-center gap-2 ${
              activeTab === 'branches'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Branch & Contact Locations ({branches.length})
          </button>
        </div>

        {/* TAB 1: KYC DOCUMENTS CHECKLIST */}
        {activeTab === 'kyc' && (
          <div className="space-y-6">
            {/* Progress Card */}
            <Card className="bg-white rounded-xl p-5 border border-slate-200/80 shadow-sm">
              <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-2">
                <span>Attached Documents: {completedCount} applicable documents uploaded</span>
                <span className="text-slate-900 font-bold">{progressPercent}%</span>
              </div>
              <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 ${isFullyVerified ? 'bg-emerald-500' : 'bg-blue-600'}`}
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </Card>

            {/* Entity Applicable Notice */}
            <div className="p-3.5 bg-blue-50/80 border border-blue-200 rounded-xl text-xs text-blue-900 flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
              <div>
                <span className="font-semibold">Upload whatever applies to your company:</span> Different business types (Pvt Ltd, LLP, Partnership, Proprietorship) require different documents. Upload whichever documents apply to your entity structure. Documents not applicable to you can simply be skipped.
              </div>
            </div>

            {/* Document Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeRequiredDocs.map((docConfig) => {
                const uploaded = getUploadedDoc(docConfig);
                const isUploading = uploadingDoc === docConfig.key;

                return (
                  <Card
                    key={docConfig.key}
                    className={`rounded-xl border p-5 flex flex-col justify-between transition-all ${
                      uploaded
                        ? 'bg-emerald-50/40 border-emerald-300 shadow-sm'
                        : 'bg-white border-slate-200/90 hover:border-blue-400 hover:shadow-sm'
                    }`}
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="font-bold text-sm text-slate-900 flex items-center gap-2">
                          {uploaded ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          ) : (
                            <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                          )}
                          {docConfig.title}
                        </div>
                        {uploaded ? (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 uppercase">
                            ✓ Attached
                          </span>
                        ) : (
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 uppercase">
                            As Applicable
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mb-4 leading-relaxed">
                        {docConfig.desc}
                      </p>
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                      {uploaded ? (
                        <>
                          <div className="truncate max-w-[200px] text-xs text-slate-700 font-medium">
                            {uploaded.file_name}
                          </div>
                          <div className="flex items-center gap-2">
                            {/* If Re-KYC is requested or not locked, allow replacing */}
                            {!isLocked && (
                              <div>
                                <input
                                  type="file"
                                  id={`replace-${docConfig.key}`}
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files?.[0]) {
                                      handleUploadDoc(docConfig.key, e.target.files[0]);
                                      e.target.value = '';
                                    }
                                  }}
                                  disabled={isUploading}
                                />
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  disabled={isUploading}
                                  onClick={() => {
                                    const input = document.getElementById(`replace-${docConfig.key}`);
                                    if (input) input.click();
                                  }}
                                  className="h-8 text-xs text-blue-600 border-blue-200 hover:bg-blue-50"
                                >
                                  {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Re-upload'}
                                </Button>
                              </div>
                            )}
                            {isLocked && (
                              <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                <Lock className="w-3 h-3 text-slate-400" /> Locked
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="w-full">
                          <input
                            type="file"
                            id={`public-upload-${docConfig.key}`}
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files?.[0]) {
                                handleUploadDoc(docConfig.key, e.target.files[0]);
                                e.target.value = '';
                              }
                            }}
                            disabled={isLocked || isUploading}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={isLocked || isUploading}
                            onClick={() => {
                              const input = document.getElementById(`public-upload-${docConfig.key}`);
                              if (input) input.click();
                            }}
                            className="w-full text-xs font-semibold bg-amber-50 hover:bg-amber-100 text-amber-900 border-amber-300 h-9"
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
                                Uploading {docConfig.label}...
                              </>
                            ) : (
                              <>
                                <Upload className="w-3.5 h-3.5 mr-2" />
                                Choose File & Upload {docConfig.label}
                              </>
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Submit & Lock Button */}
            <div className="bg-white rounded-xl p-6 border border-slate-200/80 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h4 className="font-bold text-slate-900 font-manrope text-base">
                  Ready to Finalize Your KYC Onboarding?
                </h4>
                <p className="text-xs text-slate-500 mt-0.5">
                  Once submitted, all documents will be securely locked and routed to the compliance team for approval.
                </p>
              </div>

              <Button
                type="button"
                onClick={handleSubmitKyc}
                disabled={isLocked || completedCount === 0 || submitting}
                className="bg-accent-green hover:bg-accent-green/90 text-white font-semibold font-ibm px-6 h-11 rounded-lg shrink-0"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : isLocked ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    KYC Submitted & Locked
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit KYC for Verification
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* TAB 2: BRANCH DETAILS & BRANCH CONTACT DETAILS */}
        {activeTab === 'branches' && (
          <div className="space-y-6">
            {branches.map((branch, bIdx) => (
              <Card key={branch.id || bIdx} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="bg-slate-50 px-6 py-3.5 border-b border-slate-200 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-900 text-sm">
                      {branch.is_head_office ? 'Head Office Branch' : `Branch #${bIdx + 1}`}
                    </span>
                    {branch.is_head_office && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 uppercase">
                        Primary
                      </span>
                    )}
                  </div>
                  {!isLocked && branches.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBranch(bIdx)}
                      className="text-red-600 hover:bg-red-50 h-8 px-2 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Remove
                    </Button>
                  )}
                </div>

                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Branch Nickname</Label>
                      <Input
                        value={branch.branch_nick_name}
                        onChange={(e) => handleBranchChange(bIdx, 'branch_nick_name', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Mumbai Head Office"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">GSTIN (Branch GST)</Label>
                      <Input
                        value={branch.gst_number || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'gst_number', e.target.value.toUpperCase())}
                        disabled={isLocked}
                        placeholder="e.g. 24ADXFS3875R1ZR"
                        className="mt-1 h-9 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">City</Label>
                      <Input
                        value={branch.city || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'city', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Ahmedabad"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-xs text-slate-700 font-medium">Address Line 1</Label>
                      <Input
                        value={branch.address_line_1 || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_1', e.target.value)}
                        disabled={isLocked}
                        placeholder="Building, Street, Landmark"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Address Line 2 (Optional)</Label>
                      <Input
                        value={branch.address_line_2 || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'address_line_2', e.target.value)}
                        disabled={isLocked}
                        placeholder="Area / Suite"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">State</Label>
                      <Input
                        value={branch.state || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'state', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. Gujarat"
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">PIN Code</Label>
                      <Input
                        value={branch.pincode || ''}
                        onChange={(e) => handleBranchChange(bIdx, 'pincode', e.target.value)}
                        disabled={isLocked}
                        placeholder="e.g. 380015"
                        className="mt-1 h-9 text-xs font-mono"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-700 font-medium">Country</Label>
                      <Input
                        value={branch.country || 'India'}
                        onChange={(e) => handleBranchChange(bIdx, 'country', e.target.value)}
                        disabled={isLocked}
                        className="mt-1 h-9 text-xs"
                      />
                    </div>
                  </div>

                  {/* Contact Person Details */}
                  <div className="pt-4 border-t border-slate-100">
                    <h5 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
                      Branch Primary Contact
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Contact Person</Label>
                        <Input
                          value={branch.contact_person || ''}
                          onChange={(e) => handleBranchChange(bIdx, 'contact_person', e.target.value)}
                          disabled={isLocked}
                          placeholder="Full Name"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Designation</Label>
                        <Input
                          value={branch.designation || ''}
                          onChange={(e) => handleBranchChange(bIdx, 'designation', e.target.value)}
                          disabled={isLocked}
                          placeholder="e.g. Logistics Manager"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Mobile Phone</Label>
                        <Input
                          value={(branch.contact_numbers && branch.contact_numbers[0]) || ''}
                          onChange={(e) => handleContactNumberChange(bIdx, 0, e.target.value)}
                          disabled={isLocked}
                          placeholder="+91 9876543210"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-700 font-medium">Official Email</Label>
                        <Input
                          value={(branch.emails && branch.emails[0]) || ''}
                          onChange={(e) => handleEmailChange(bIdx, 0, e.target.value)}
                          disabled={isLocked}
                          placeholder="logistics@company.com"
                          className="mt-1 h-9 text-xs"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}

            {!isLocked && (
              <div className="flex items-center justify-between pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={addBranch}
                  className="text-xs font-semibold text-slate-700 h-10 border-slate-300"
                >
                  <Plus className="w-4 h-4 mr-1.5" /> Add Another Branch Location
                </Button>

                <Button
                  type="button"
                  onClick={saveBranches}
                  disabled={savingBranches}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-10 px-6 rounded-lg"
                >
                  {savingBranches ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : 'Save Branch Details'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
